import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Wizard from '../../../wizard/Wizard';
import Button from '../../../button/Button';

import ConversationType, { CONVERSATION_TYPES } from './stepType/ConversationType';
import UserList from './stepOne/UserList';
import GroupDetails from './stepGroup/GroupDetails';
import Message from './stepTwo/Message';
import Confirmation from './stepThree/Confirmation';

import { START_INTERNAL_CHATS, UPSERT_INTERNAL_CHAT_GROUP } from '../../../../helpers/internalChatConstants';
import useChatDispatchActions from '../../../../customHooks/useChatDispatchActions';

import usePrevious from '../../../../customHooks/usePrevious';
import internalChatToast from '../../utils/internalChatToast';

import { checkPermissions } from '../../../../components/ctvoicer/accessControl/AccessControl';

import { messageGroupsService } from '../../../../newServices/corporateChat';
import {
    buildGroupParticipants,
    canCreateInternalGroups,
    getApiErrorMessage,
    getTokenRole,
    resolveInitialGroupMessages
} from '../../../../utils/internalChatGroups';

const STEPS = {
    TYPE: 'type',
    USERS: 'users',
    GROUP: 'group',
    MESSAGE: 'message',
    CONFIRMATION: 'confirmation'
};

const StartNewInternalChatModal = ({ show, toggle }) => {
    const [currWizardStep, setCurrWizardStep] = useState(0)
    const [selectedUsers, setSelectedUsers] = useState([])
    const [messages, setMessages] = useState([]);
    const [files, setFiles] = useState([]);
    const [conversationType, setConversationType] = useState(CONVERSATION_TYPES.INDIVIDUAL);
    const [groupName, setGroupName] = useState('');
    const [participantLevels, setParticipantLevels] = useState({});
    const [creatingGroup, setCreatingGroup] = useState(false);

    const { startNewChat } = useSelector(state => state.internalChat.isLoading);
    const { contacts } = useSelector(state => state.internalChat);
    const userData = useSelector(state => state.auth.userData);
    const accessToken = useSelector(state => state.auth.user?.access_token);

    const canAttachFiles = checkPermissions(userData, ['attach.files']);

    /** Só administrador e supervisor criam grupo. Para o agente o assistente continua
     *  como sempre foi, sem o passo "Tipo".
     */
    const canCreateGroups = React.useMemo(() => canCreateInternalGroups(getTokenRole(accessToken)), [accessToken]);
    const isGroup = canCreateGroups && conversationType === CONVERSATION_TYPES.GROUP;

    const steps = React.useMemo(() => [
        canCreateGroups && STEPS.TYPE,
        STEPS.USERS,
        isGroup && STEPS.GROUP,
        STEPS.MESSAGE,
        STEPS.CONFIRMATION
    ].filter(Boolean), [canCreateGroups, isGroup]);

    const currentStep = steps[currWizardStep];
    const isSubmitting = startNewChat || creatingGroup;

    /** A conversa pode ser iniciada só com anexo: o passo 2 fica válido com mensagem
     *  OU com arquivo. */
    const hasContent = messages.length > 0 || files.length > 0;

    const [showSelectedList, setShowSelectedList] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const intl = useIntl();

    const previousContacts = usePrevious(contacts);

    const dispatchChatActions = useChatDispatchActions();

    const goToStep = (step) => {
        /** A validação ocorre apenas quando o usuário estiver avançcando,
         *  não quando ele estiver voltando para um passo anterior.
         *  Ao voltar, remove a validação da tela que estava anteriormente.
         */
        if (step < currWizardStep) {
            setErrorMessage(null)
            setCurrWizardStep(step)
        } else {
            if (validate()) {
                setCurrWizardStep(step)
            }
        }
    }

    const validate = useCallback((color) => {
        let err = null;
        if (currentStep === STEPS.USERS && selectedUsers.length === 0) err = intl.formatMessage({ id: "Por favor, selecione pelo menos um usuário." });
        if (currentStep === STEPS.GROUP && !groupName.trim()) err = intl.formatMessage({ id: "Por favor, informe o nome do grupo." });
        /** No grupo a mensagem é opcional: ele pode ser criado vazio. */
        if (currentStep === STEPS.MESSAGE && !isGroup && !hasContent) err = intl.formatMessage({ id: "Por favor, adicione pelo menos 1 mensagem ou anexo." });
        setErrorMessage(err);
        return err ? false : true;
    }, [selectedUsers, hasContent, currentStep, groupName, isGroup]);

    const toggleSelectUser = useCallback((user) => {
        const userPreviouslySelected = typeof selectedUsers.find(u => u.id === user.id && u.groupId !== user.groupId) === 'object'
        console.log(user)
        if(userPreviouslySelected){
            alert('Usuário já selecionado em outro grupo')
            return
        }

        setSelectedUsers(prevState => {
            const newState = [...prevState]
            if (newState.includes(user)) {
                const index = newState.indexOf(user)
                newState.splice(index, 1)
            } else {
                newState.push(user)
            }
            return newState
        })

        setErrorMessage(null)
    }, [])

    const handleRemoveUser = (user) => {
        setSelectedUsers(prevState => {
            const newState = [...prevState]
            const index = newState.indexOf(user)
            newState.splice(index, 1)
            return newState
        })
    }

    const handleChangeConversationType = (type) => {
        setConversationType(type)
        setErrorMessage(null)
    }

    const handleChangeGroupName = (name) => {
        setGroupName(name)
        setErrorMessage(null)
    }

    const handleChangeParticipantLevel = useCallback((userId, level) => {
        setParticipantLevels(prevState => ({
            ...prevState,
            [userId]: level
        }))
    }, [])

    const resetWizard = () => {
        setFiles([])
        setMessages([])
        setSelectedUsers([])
        setErrorMessage(null)
        setCurrWizardStep(0)
        setConversationType(CONVERSATION_TYPES.INDIVIDUAL)
        setGroupName('')
        setParticipantLevels({})
    }

    /** O modal fica montado depois do primeiro uso (SmartRendering nunca volta a false),
     *  então cancelar sem limpar deixaria os anexos escolhidos aqui e eles seriam
     *  enviados na próxima conversa que o usuário iniciasse. */
    const handleCancel = () => {
        resetWizard()
        toggle()
    }

    const handleStartNewInternalChat = ({selectedUsers, messages, files}) => {
        dispatchChatActions(START_INTERNAL_CHATS, {
            selectedUsers: selectedUsers.map( user => {
                                return {
                                    ...user,
                                    userId: user.id
                                }
                            }),
                            messages,
                            files
                        })
    }

    /** A resposta da criação já traz o grupo completo (participantes e mensagens
     *  iniciais): ele entra direto na lista, sem nova busca.
     */
    const handleCreateGroup = async () => {
        setCreatingGroup(true)
        try {
            const { data } = await messageGroupsService.postMessageGroups({
                name: groupName,
                participants: buildGroupParticipants(selectedUsers, participantLevels),
                messages: resolveInitialGroupMessages(messages, files),
                files: files
            })

            if (!data) throw new Error('Grupo não retornado pela API');

            dispatchChatActions(UPSERT_INTERNAL_CHAT_GROUP, data)
            internalChatToast.success(intl.formatMessage({ id: "Grupo criado com sucesso!" }))
            resetWizard()
            toggle()
        } catch (error) {
            internalChatToast.error(
                getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível criar o grupo. Tente novamente." })
            )
        } finally {
            setCreatingGroup(false)
        }
    }

    const handleConfirm = () => {
        if (isGroup) {
            handleCreateGroup()
        } else {
            handleStartNewInternalChat({ selectedUsers, messages, files })
        }
    }

    useEffect(() => {
        if (show && previousContacts && (previousContacts?.length !== contacts?.length)) {
            toggle()
            resetWizard()
        }
    }, [contacts, previousContacts, show])

    useEffect(() => {
        if (selectedUsers.length === 0 && showSelectedList) {
            setShowSelectedList(false)
        }
    }, [selectedUsers, showSelectedList])

    return (
        <Wizard
            show={show}
            title="Iniciar nova conversa interna"
            currStep={currWizardStep}
            onStepChange={goToStep}
            // onWizardAbort={toggle}
            errorMessage={errorMessage}
            onClick={goToStep}>
            {canCreateGroups &&
                <Wizard.Step active={currentStep === STEPS.TYPE} title="Tipo">
                    <Wizard.StepContent>
                        <ConversationType
                            conversationType={conversationType}
                            setConversationType={handleChangeConversationType} />
                    </Wizard.StepContent>
                </Wizard.Step>
            }
            <Wizard.Step active={currentStep === STEPS.USERS} title={isGroup ? "Participantes" : "Usuários"}>
                <Wizard.StepContent>
                    <UserList
                        show={show}
                        selectedUsers={selectedUsers}
                        handleRemoveUser={handleRemoveUser}
                        toggleSelectUser={toggleSelectUser}
                        setErrorMessage={setErrorMessage} />

                </Wizard.StepContent>
            </Wizard.Step>
            {isGroup &&
                <Wizard.Step active={currentStep === STEPS.GROUP} title="Grupo">
                    <Wizard.StepContent>
                        <GroupDetails
                            groupName={groupName}
                            setGroupName={handleChangeGroupName}
                            selectedUsers={selectedUsers}
                            participantLevels={participantLevels}
                            setParticipantLevel={handleChangeParticipantLevel} />
                    </Wizard.StepContent>
                </Wizard.Step>
            }
            <Wizard.Step active={currentStep === STEPS.MESSAGE} title="Mensagem">
                <Wizard.StepContent>
                    <Message
                        messages={messages}
                        setMessages={setMessages}
                        files={files}
                        setFiles={setFiles}
                        canAttachFiles={canAttachFiles}
                     />
                </Wizard.StepContent>
            </Wizard.Step>
            <Wizard.Step active={currentStep === STEPS.CONFIRMATION} title="Confirmação">
                <Wizard.StepContent>
                    <Confirmation
                        selectedUsers={selectedUsers}
                        messages={messages}
                        files={files}
                        isGroup={isGroup}
                        groupName={groupName}
                        toggle={toggle}
                    />
                </Wizard.StepContent>
            </Wizard.Step>
            <Wizard.Footer>
                <div className="d-flex justify-content-between">
                    <Button.Cancel
                        disabled={isSubmitting}
                        onClick={handleCancel} />
                    <div className="d-flex">
                        {currWizardStep > 0 &&
                            <Button.Prev
                                disabled={isSubmitting}
                                className="mr-50"
                                onClick={() => goToStep(currWizardStep - 1)} />
                        }
                        {currentStep === STEPS.CONFIRMATION ?
                            <Button.Confirm
                                disabled={isSubmitting}
                                loading={isSubmitting}
                                dark
                                onClick={handleConfirm} />
                            :
                            <Button.Next
                                dark
                                onClick={() => goToStep(currWizardStep + 1)} />
                        }
                    </div>
                </div>
            </Wizard.Footer>
        </Wizard>
    )
}

export default React.memo(StartNewInternalChatModal);
